import React, { useContext, useState, useRef, useEffect } from "react";
import {
  RiGitBranchLine,
} from "react-icons/ri";
import { FaWhatsapp } from "react-icons/fa";
import { SiAwsorganizations } from "react-icons/si";
import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { TbChartBar, TbListCheck } from "react-icons/tb";
import { FiGrid, FiSettings, FiHelpCircle, FiPhone, FiCreditCard, FiBell, FiCalendar } from "react-icons/fi";
import { IoChevronUpOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { useLocation, useNavigate } from "react-router-dom";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import callCenterTranslations from "../common/CallCenterTranslations";
import { createPortal } from "react-dom";
import {
  hasScreenAccess,
  getStoredPermissions,
  PERMISSIONS_CHANGED_EVENT,
} from "../../utils/permissions";
import {
  isCallingEnabled,
  isWebCallingEnabled,
  ORG_FEATURES_CHANGED_EVENT,
} from "../../utils/orgFeatures";
import { getAlertSummary } from "../../service/CallCenterAlerts";
import { AiOutlineHome  } from "react-icons/ai";
import { LuUsers } from "react-icons/lu";
import { ImStatsBars } from "react-icons/im";
import { PiListChecks } from "react-icons/pi";
import { LuPhone } from "react-icons/lu";
import { WASL_APP_NAME, WASL_LOGO } from "../../branding/waslBrand";

const Tooltip = ({ children, text, langMode = "en" }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef();

  useEffect(() => {
    if (visible && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top + rect.height / 2,
        left: langMode === "ar" ? rect.left - 12 : rect.right + 12,
      });
    }
  }, [visible, langMode]);

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="relative flex w-full justify-center"
      >
        {children}
      </div>
      {visible &&
        createPortal(
          <div
            className="fixed z-[9999999] rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg"
            style={{
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              transform: langMode === "ar" ? "translate(-100%, -50%)" : "translateY(-50%)",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DashboardMenus = ({ setShowNames = () => {}, showNames = false }) => {
  const { theme, langMode, userType, currentLocationRef } = useContext(AppContext);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isExpanded = showNames || isSidebarOpen;
  const [expandedMenus, setExpandedMenus] = useState({});
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [screenSize, setScreenSize] = useState(false);
  const [orgFeaturesVersion, setOrgFeaturesVersion] = useState(0);
  const [alertAttentionCount, setAlertAttentionCount] = useState(0);
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  void orgFeaturesVersion;
  void permissionsVersion;
  const storedPermissions = getStoredPermissions();
  const fullAccess = storedPermissions?.fullAccess ?? false;
  const hasAccess = (screenCode) => fullAccess || hasScreenAccess(screenCode, "canView");
  const callingEnabled = isCallingEnabled();

  useEffect(() => {
    const onOrgFeaturesChanged = () => setOrgFeaturesVersion((value) => value + 1);
    window.addEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
    return () => window.removeEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
  }, []);

  useEffect(() => {
    const onPermissionsChanged = () => setPermissionsVersion((value) => value + 1);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, onPermissionsChanged);
    return () => window.removeEventListener(PERMISSIONS_CHANGED_EVENT, onPermissionsChanged);
  }, []);

  useEffect(() => {
    if (!hasAccess("CC_AGENT_ALERTS")) {
      setAlertAttentionCount(0);
      return undefined;
    }
    const refreshAlertBadge = async () => {
      try {
        const summary = await getAlertSummary();
        setAlertAttentionCount(Number(summary?.attentionCount ?? summary?.attention ?? 0));
      } catch {
        setAlertAttentionCount(0);
      }
    };
    refreshAlertBadge();
    const timerId = window.setInterval(refreshAlertBadge, 30000);
    return () => window.clearInterval(timerId);
  }, [fullAccess]);

  useEffect(() => {
    const onResize = () => setScreenSize(window.innerWidth <= 1213);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const ccT = (key) =>
    callCenterTranslations[langMode]?.[key] || callCenterTranslations.en[key] || key;

  const callCenterMenus = [
    {
      link: "/call-center/dashboard",
      name: ccT("ccMenuAnalytics"),
      tooltip: ccT("ccMenuAnalytics"),
      screenCode: "CC_DASHBOARD",
      icon: <AiOutlineHome  size={20} />,
      relatedPaths: ["/call-center", "/call-center/dashboard", "/call-center/analytics"],
    },
    {
      link: "/call-center/weekly-tracker",
      name: ccT("ccMenuWeeklyTracker"),
      tooltip: ccT("ccMenuWeeklyTracker"),
      screenCode: "CC_WEEKLY_TRACKER",
      icon: <ImStatsBars size={20} />,
      relatedPaths: ["/call-center/weekly-tracker"],
    },
    {
      link: "/call-center/tickets",
      name: ccT("ccMenuTickets"),
      tooltip: ccT("ccMenuTickets"),
      screenCode: "CC_TICKETS",
      icon: <PiListChecks size={20} />,
      children: [
        { link: "/call-center/tickets", name: ccT("ccMenuTickets"), screenCode: "CC_TICKETS" },
        { link: "/call-center/agent-workspace", name: ccT("ccMenuAgentWorkspace"), screenCode: "CC_AGENT_WORKSPACE" },
        { link: "/call-center/ticket-bulk-upload", name: ccT("ccMenuTicketBulkUpload"), screenCode: "CC_AGENT_WORKSPACE" },
        { link: "/call-center/ticket-upload-history", name: ccT("ccMenuTicketUploadHistory"), screenCode: "CC_AGENT_WORKSPACE" },
        {
          link: "/call-center/ticket-settings",
          name: ccT("ccMenuTicketSettingsMerged"),
          screenCodes: ["CC_TICKET_COLUMNS", "CC_SETTINGS"],
          relatedPaths: [
            "/call-center/ticket-settings",
            "/call-center/ticket-columns",
            "/call-center/settings",
          ],
        },
      ],
    },
    {
      link: "/call-center/account-plans",
      name: ccT("ccMenuAccountPlans"),
      tooltip: ccT("ccMenuAccountPlans"),
      screenCode: "CC_ACCOUNT_PLAN",
      icon: <FiCalendar size={20} />,
      children: [
        {
          link: "/call-center/account-plans",
          name: ccT("ccMenuAccountPlans"),
          screenCode: "CC_ACCOUNT_PLAN",
        },
        {
          link: "/call-center/account-plan-types",
          name: ccT("ccMenuAccountPlanTypes"),
          screenCode: "CC_ACCOUNT_PLAN",
        },
      ],
    },
    {
      link: "/call-center/notifications/reminders",
      name: ccT("ccMenuNotifications"),
      tooltip: ccT("ccMenuNotifications"),
      screenCode: "CC_PAYMENT_REMINDERS",
      icon: <FiBell size={20} />,
      relatedPaths: [
        "/call-center/notifications",
        "/call-center/notifications/reminders",
        "/call-center/notifications/confirmations",
        "/call-center/alerts",
      ],
      children: [
        {
          link: "/call-center/notifications/reminders",
          name: ccT("ccMenuPaymentReminders"),
          screenCode: "CC_PAYMENT_REMINDERS",
        },
        {
          link: "/call-center/notifications/confirmations",
          name: ccT("ccMenuPaymentConfirmations"),
          screenCode: "CC_PAYMENT_CONFIRMATIONS",
        },
        {
          link: "/call-center/alerts",
          name: ccT("ccMenuAgentResponseAlerts"),
          screenCode: "CC_AGENT_ALERTS",
        },
      ],
    },
    {
      link: "/call-center/browser-phone",
      name: ccT("ccMenuBrowserPhone"),
      tooltip: ccT("ccMenuBrowserPhone"),
      screenCode: "CC_INBOX",
      icon: <LuPhone size={20} />,
    },
    {
      link: "/call-center/campaigns",
      name: ccT("ccMenuCampaigns"),
      tooltip: ccT("ccMenuCampaigns"),
      screenCode: "CC_CAMPAIGNS",
      icon: <RiGitBranchLine size={20} />,
    },
    {
      link: "/call-center/whatsapp-settings",
      name: ccT("ccMenuWhatsAppSettings"),
      tooltip: ccT("ccMenuWhatsAppSettings"),
      screenCode: "CC_WHATSAPP_SETTINGS",
      icon: <FaWhatsapp size={20} />,
      relatedPaths: ["/call-center/whatsapp-settings"],
    },
    {
      link: "/call-center/payment-confirmation-settings",
      name: ccT("ccMenuPaymentConfirmationSettings"),
      tooltip: ccT("ccMenuPaymentConfirmationSettings"),
      screenCode: "CC_PAYMENT_CONFIRMATION_SETTINGS",
      icon: <FiCreditCard size={20} />,
      relatedPaths: ["/call-center/payment-confirmation-settings"],
    },
  ]
    .filter((item) => {
      if (!callingEnabled) {
        return item.link !== "/call-center/browser-phone" && item.link !== "/call-center/campaigns";
      }
      if (!isWebCallingEnabled() && item.link === "/call-center/browser-phone") {
        return false;
      }
      return true;
    })
    .map((item) => {
      const childHasAccess = (child) => {
        if (Array.isArray(child.screenCodes)) {
          return child.screenCodes.some((code) => hasAccess(code));
        }
        return hasAccess(child.screenCode);
      };
      const children = (item.children || [])
        .filter((child) => childHasAccess(child))
        .map((child) => ({ ...child, relatedPaths: child.relatedPaths || [child.link] }));

      const hasOwnAccess = hasAccess(item.screenCode);
      if (!hasOwnAccess && children.length === 0) return null;

      return {
        icon: item.icon,
        link: hasOwnAccess ? item.link : children[0]?.link,
        relatedPaths: item.relatedPaths || [
          ...(hasOwnAccess ? [item.link] : []),
          ...children.map((c) => c.link),
        ],
        name: item.name,
        tooltip: item.tooltip,
        screenCode: item.screenCode,
        children,
        isPermission: true,
      };
    })
    .filter(Boolean);

  const dashboardMenu = callCenterMenus.find((m) => m.link === "/call-center/dashboard");
  const otherCallCenterMenus = callCenterMenus.filter((m) => m.link !== "/call-center/dashboard");

  const menus = [
    ...(dashboardMenu ? [dashboardMenu] : []),
    ...(userType === "super_admin"
      ? [{
          icon: <SiAwsorganizations size={20} />,
          link: "/organizations",
          relatedPaths: ["/organizations"],
          name: ccT("ccMenuOrganizations"),
          tooltip: ccT("ccMenuOrganizationsTooltip"),
          screenCode: "ORG_MANAGEMENT",
          isPermission: true,
        }]
      : []),
    {
      icon: <LuUsers size={20} />,
      link: "/all-users",
      relatedPaths: ["/all-users", "/add-user"],
      name: translations[langMode].userlist,
      tooltip: translations[langMode].usertooltip,
      screenCode: "USER_MANAGEMENT",
      isPermission: hasAccess("USER_MANAGEMENT"),
    },
    {
      icon: <MdOutlineAdminPanelSettings size={20} />,
      link: "/role-management",
      relatedPaths: ["/role-management"],
      name: translations[langMode].roleManagement,
      tooltip: translations[langMode].roleManagementtooltip,
      screenCode: "ROLE_MANAGEMENT",
      isPermission: hasAccess("ROLE_MANAGEMENT"),
    },
    ...otherCallCenterMenus,
  ];

  const handleNavigation = (nextPath, onClickHandler) => {
    if (onClickHandler) { onClickHandler(); return; }
    const prevPath = currentLocationRef.current?.pathname || location.pathname;
    if (prevPath?.startsWith("/update-complete-task") && prevPath !== nextPath) {
      window.dispatchEvent(new CustomEvent("blockNavigation", { detail: { nextPath } }));
      return;
    }
    currentLocationRef.current = { pathname: nextPath };
    navigate(nextPath);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setShowNames(false);
    };
    if (showNames) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNames, setShowNames]);

  useEffect(() => { setIsSidebarOpen(false); }, [location]);

  useEffect(() => {
    setExpandedMenus((current) => {
      let changed = false;
      const next = { ...current };
      callCenterMenus.forEach((menu) => {
        if (!menu.children?.length || !menu.link) return;
        if (menu.children.some((c) => location.pathname === c.link) && !next[menu.link]) {
          next[menu.link] = true;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [location.pathname]);

  const filteredMenus = menus.filter((m) => m.isPermission !== false);
  const isCollapsed = !showNames && !isSidebarOpen;

  const toggleMenu = (menuLink, forceOpen) => {
    setExpandedMenus((c) => ({
      ...c,
      [menuLink]: typeof forceOpen === "boolean" ? forceOpen : !c[menuLink],
    }));
  };

  const renderMenuItem = (menu) => {
    const isActive =
      location.pathname === menu.link ||
      (menu.relatedPaths || []).includes(location.pathname);
    const visibleChildren = Array.isArray(menu.children) ? menu.children : [];
    const isMenuExpanded = visibleChildren.length > 0 && (expandedMenus[menu.link] || isActive);

    const itemButton = (
      <button
        type="button"
        onClick={() => {
          if (visibleChildren.length > 0) {
            if (isCollapsed) {
              setShowNames(true);
              toggleMenu(menu.link, true);
              return;
            }
            toggleMenu(menu.link);
            return;
          }
          handleNavigation(menu.link, menu.onClick);
        }}
        className={cn(
          "group flex items-center rounded-xl text-[13px] font-medium transition-all duration-200",
          isCollapsed
            ? "h-11 w-11 shrink-0 justify-center"
            : "h-11 w-full gap-3 px-3",
          isActive
            ? "bg-[#EEF4FF] text-[#236DB4]"
            : "text-slate-600 hover:bg-[#EEF4FF] hover:text-[#236DB4]"
        )}
      >
        <span className={cn(
          "relative flex shrink-0 items-center justify-center",
          isCollapsed ? "h-5 w-5" : "h-7 w-7 rounded-lg",
          isActive
            ? "text-[#236DB4]"
            : "text-slate-500 group-hover:text-[#236DB4]"
        )}>
          {React.isValidElement(menu.icon)
            ? React.cloneElement(menu.icon, { size: 18 })
            : menu.icon}
          {menu.link === "/call-center/notifications/reminders" && alertAttentionCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4A00] px-1 text-[10px] font-bold text-white">
              {alertAttentionCount > 99 ? "99+" : alertAttentionCount}
            </span>
          )}
        </span>
        <div
          className={cn(
            "flex items-center gap-2 overflow-hidden transition-all duration-300",
            isCollapsed
              ? "w-0 min-w-0 max-w-0 flex-none opacity-0"
              : "flex-1 max-w-[220px] opacity-100"
          )}
        >
          <span className="truncate text-left">{menu.name}</span>
          {visibleChildren.length > 0 && (
            <IoChevronUpOutline
              size={14}
              className={cn(
                "shrink-0 transition-transform duration-200",
                !isMenuExpanded && "rotate-180",
                isActive ? "text-[#236DB4]" : "text-slate-400"
              )}
            />
          )}
        </div>
      </button>
    );

    return (
      <div
        key={menu.link}
        className={cn(
          menu.isPermission === false && "hidden",
          isCollapsed && "flex w-full justify-center"
        )}
      >
        {isCollapsed ? (
          <Tooltip text={menu.tooltip || menu.name} langMode={langMode}>
            {itemButton}
          </Tooltip>
        ) : (
          <div>
            {itemButton}
            {visibleChildren.length > 0 && isMenuExpanded && (
              <div className="ml-[22px] mt-0.5 space-y-0.5 border-l-2 border-blue-100 pl-3">
                {visibleChildren.map((child) => {
                  const isChildActive =
                    location.pathname === child.link ||
                    (child.relatedPaths || []).includes(location.pathname);
                  return (
                    <button
                      key={child.link}
                      type="button"
                      className={cn(
                        "block w-full rounded-lg py-2 pl-3 text-left text-[13px] transition-colors",
                        isChildActive
                          ? "font-semibold text-[#3b82f6]"
                          : "text-slate-500 hover:text-[#3b82f6]"
                      )}
                      onClick={() => handleNavigation(child.link, child.onClick)}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span>{child.name}</span>
                        {child.screenCode === "CC_AGENT_ALERTS" && alertAttentionCount > 0 && (
                          <span className="rounded-full bg-[#EF4A00] px-1.5 py-0.5 text-[10px] font-bold text-white">
                            {alertAttentionCount > 99 ? "99+" : alertAttentionCount}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const settingsPath = hasAccess("CC_SETTINGS") ? "/call-center/sla-settings" : null;
  const isSettingsActive =
    location.pathname === "/call-center/sla-settings" ||
    location.pathname === "/call-center/ticket-settings" ||
    location.pathname === "/call-center/settings" ||
    location.pathname === "/call-center/ticket-columns" ||
    location.pathname === "/call-center/whatsapp-settings" ||
    location.pathname === "/call-center/payment-confirmation-settings";

  const bottomLinks = [
    ...(settingsPath
      ? [{ icon: <FiSettings size={18} />, label: ccT("ccMenuSettings"), path: settingsPath }]
      : []),
    { icon: <FiHelpCircle size={18} />, label: "Help & Support", path: null },
  ];

  return (
    <>
      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {screenSize && (
        <button
          type="button"
          className="lg:hidden flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm"
          onClick={() => setIsSidebarOpen((prev) => !prev)}
          aria-label="Toggle navigation"
        >
          {isSidebarOpen ? <RxCross2 size={18} /> : <FiGrid size={18} />}
        </button>
      )}

      <div
        ref={sidebarRef}
        className={cn(
          "flex h-full flex-col",
          theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "",
          isSidebarOpen
            ? "fixed left-0 top-0 z-50 h-screen w-[260px] bg-white p-4 shadow-2xl lg:static lg:h-full lg:w-full lg:bg-transparent lg:p-0 lg:shadow-none"
            : screenSize
              ? "hidden lg:flex"
              : "w-full"
        )}
      >
        {/* Logo + brand name */}
        <button
          type="button"
          onClick={() => setShowNames(!showNames)}
          className={cn(
            "mb-5 flex w-full cursor-pointer items-center",
            isCollapsed ? "justify-center" : "gap-2.5 px-2"
          )}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <img
            src={WASL_LOGO}
            alt={WASL_APP_NAME}
            className="h-8 w-8 object-contain"
          />
          <span
            className={cn(
              "overflow-hidden whitespace-nowrap text-lg font-bold tracking-tight text-slate-800 transition-all duration-300",
              isCollapsed ? "w-0 opacity-0" : "opacity-100"
            )}
          >
            {WASL_APP_NAME}
          </span>
        </button>

        {/* Active indicator track on left edge */}
        <nav
          className={cn(
            "custom-side-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto",
            isCollapsed ? "items-center px-0" : "px-1"
          )}
        >
          {filteredMenus.map((menu) => {
            const isActive =
              location.pathname === menu.link ||
              (menu.relatedPaths || []).includes(location.pathname);

            return (
              <div
                key={menu.link}
                className={cn("relative w-full", isCollapsed && "flex justify-center")}
              >
                {isActive && !isCollapsed && (
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 h-7 w-1 rounded-r-full bg-[#2B7FFF]" />
                )}
                {renderMenuItem(menu)}
              </div>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="mt-auto border-t border-slate-100 pt-3 px-1">
          {bottomLinks.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => item.path && handleNavigation(item.path)}
              className={cn(
                "flex items-center rounded-xl text-[13px] font-medium transition",
                item.path === settingsPath && isSettingsActive
                  ? "bg-[#EEF4FF] text-[#236DB4]"
                  : "text-slate-500 hover:bg-[#eef4ff] hover:text-[#236DB4]",
                isCollapsed
                  ? "mx-auto h-11 w-11 justify-center"
                  : "w-full gap-3 px-3 py-2.5"
              )}
            >
              <span
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center",
                  item.path === settingsPath && isSettingsActive
                    ? "text-[#236DB4]"
                    : "text-slate-500"
                )}
              >
                {item.icon}
              </span>
              <span
                className={cn(
                  "overflow-hidden whitespace-nowrap transition-all duration-300",
                  isCollapsed ? "w-0 opacity-0" : "opacity-100"
                )}
              >
                {item.label}
              </span>
            </button>
          ))}

          {callingEnabled && (
          <div
            className={cn(
              "overflow-hidden transition-all duration-300",
              isCollapsed
                ? "max-h-0 opacity-0 pointer-events-none"
                : "max-h-[280px] opacity-100 mt-3"
            )}
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#1D4ED8] to-[#1E3A8A] p-4 text-white">
              <div className="relative">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#2B7FFF] text-white shadow-[0_6px_16px_rgba(43,127,255,0.35)]">
                  <FiPhone size={18} strokeWidth={2.25} />
                </div>
                <p className="text-sm font-bold">{ccT("ccCampaignPromoTitle")}</p>
                <p className="mt-1 text-xs leading-relaxed text-white/75">
                  {ccT("ccCampaignPromoSubtitle")}
                </p>
                <button
                  type="button"
                  onClick={() => handleNavigation("/call-center/campaigns")}
                  className="mt-3 w-full rounded-xl bg-[#2B7FFF] py-2 text-sm font-semibold text-white transition hover:bg-[#236DB4]"
                >
                  {ccT("ccCampaignPromoAction")}
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardMenus;
